/** @format */

import React, { useState, useEffect, lazy, Suspense } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BookOpenIcon,
  EyeIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  XMarkIcon,
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { useMyBooks } from "../hooks/useMyBooks";
import { Book } from "../types";
import { formatDate } from "../utils/formatUtils";
import { getImageUrl } from "../utils/pdfUtils";
import "../components/SecurePDFViewer.css";
import AIChatButton from '../components/AIChatButton';

// Lazy load WebViewPdfViewer component
const WebViewPdfViewer = lazy(() => import('../components/WebViewPdfViewer'));

const MyBooks: React.FC = () => {
  const { purchasedBooks, loading, error, refreshBooks } = useMyBooks();

  const [selectedBook, setSelectedBook] = useState<any>(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFullScreen, setShowFullScreen] = useState(false);
  
  // Reading progress tracking
  const [readingProgress, setReadingProgress] = useState<{[key: string]: number}>({});
  const [readingSessions, setReadingSessions] = useState<{[key: string]: any[]}>({});
  const [bookTotalPages, setBookTotalPages] = useState<{[key: string]: number}>({});

  // Handle deep-link open from query params
  const location = useLocation();
  const navigate = useNavigate();
  const [returnTo, setReturnTo] = useState<string | null>(null);
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openId = params.get('openId');
    const openTitle = params.get('openTitle');
    const openUrl = params.get('openUrl');
    const rt = params.get('returnTo');
    if (rt) setReturnTo(rt);
    if (openId && openUrl) {
      // Try to find book from purchased list, else construct minimal book object
      const found = purchasedBooks.find((b: any) => (b.id === openId));
      const bookToOpen = found || { id: openId, title: openTitle || 'Resource', pdf_url: openUrl, author: 'Unknown' };
      handleViewBook(bookToOpen);
      // Clean the URL so back button won't reopen the viewer
      navigate('/app/books', { replace: true });
    }
  }, [location.search, purchasedBooks]);
  


  // Load reading progress from localStorage
  useEffect(() => {
    // Only load progress when books are loaded and not loading
    if (loading || purchasedBooks.length === 0) {
      return;
    }
    
    try {
      const savedProgress: {[key: string]: number} = {};
      const savedSessions: {[key: string]: any[]} = {};
      const savedTotalPages: {[key: string]: number} = {};
      
      // Load progress for each book
      purchasedBooks.forEach(book => {
        const progress = localStorage.getItem(`reading_progress_${book.id}`);
        if (progress) {
          savedProgress[book.id] = parseInt(progress);
        }
        
        const sessions = localStorage.getItem(`reading_sessions_${book.id}`);
        if (sessions) {
          savedSessions[book.id] = JSON.parse(sessions);
        }

        const totalPages = localStorage.getItem(`total_pages_${book.id}`);
        if (totalPages) {
          savedTotalPages[book.id] = parseInt(totalPages);
        }
      });
      
      // Initialize progress for books that don't have any
      const initializedProgress = { ...savedProgress };
      purchasedBooks.forEach(book => {
        if (!(book.id in initializedProgress)) {
          initializedProgress[book.id] = 0;
        }
      });
      
      setReadingProgress(initializedProgress);
      setReadingSessions(savedSessions);
      setBookTotalPages(savedTotalPages);
    } catch (error) {
      console.error('Error loading reading progress:', error);
    }
  }, [loading, purchasedBooks]);

  // Handle viewing a book
  const handleViewBook = async (book: any) => {
    if (book.pdf_url) {
      setSelectedBook(book);
      setShowPdfViewer(true);
      try {
        // Try to resume from last saved page
        const savedLastPageStr = localStorage.getItem(`last_page_${book.id}`);
        const savedTotalPagesStr = localStorage.getItem(`total_pages_${book.id}`);
        const savedLastPage = savedLastPageStr ? parseInt(savedLastPageStr, 10) : 1;
        const savedTotalPages = savedTotalPagesStr ? Math.max(1, parseInt(savedTotalPagesStr, 10)) : undefined;
        const resumePage = Math.max(1, savedTotalPages ? Math.min(savedLastPage || 1, savedTotalPages) : (savedLastPage || 1));
        setCurrentPage(resumePage);
      } catch {
        setCurrentPage(1);
      }
      
      // Start reading session
      startReadingSession(book.id);
    } else {
      toast.error('View link not available for this book.');
    }
  };

  // Handle page change from PDF viewer
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    if (selectedBook) {
      const totalPages = bookTotalPages[selectedBook.id] || 1;
      
      // Ensure page number doesn't exceed total pages
      const validPageNumber = Math.min(pageNumber, totalPages);
      
      updateReadingProgress(selectedBook.id, validPageNumber, totalPages);
      try {
        localStorage.setItem(`last_page_${selectedBook.id}`, String(Math.max(1, validPageNumber)));
      } catch {}
      
      // Auto-complete book when user reaches the last page
      if (validPageNumber === totalPages && totalPages > 1) {
        toast.success('🎉 Congratulations! You have completed this book!');
        
        // End the current reading session
        endReadingSession(selectedBook.id);
      }
    }
  };

  // Handle document load from PDF viewer
  const handleDocumentLoad = (numPages: number) => {
    if (selectedBook) {
      // Update reading progress with actual page count
      updateReadingProgress(selectedBook.id, currentPage, numPages);
      setBookTotalPages(prev => ({ ...prev, [selectedBook.id]: numPages }));
    }
  };

  // Reading progress functions
  const updateReadingProgress = (bookId: string, currentPage: number, totalPages: number) => {
    // Ensure values are valid
    const validCurrentPage = Math.max(1, Math.min(currentPage, totalPages));
    const validTotalPages = Math.max(1, totalPages);
    
    // Calculate progress and ensure it's between 0-100
    const progress = Math.min(Math.max(Math.round((validCurrentPage / validTotalPages) * 100), 0), 100);
    
    setReadingProgress(prev => ({
      ...prev,
      [bookId]: progress
    }));
    
    // Save to localStorage
    localStorage.setItem(`reading_progress_${bookId}`, progress.toString());
    localStorage.setItem(`total_pages_${bookId}`, validTotalPages.toString());
  };

  const markBookAsCompleted = (bookId: string) => {
    const totalPages = bookTotalPages[bookId] || 1;
    updateReadingProgress(bookId, totalPages, totalPages);
    
    // End any active reading session
    endReadingSession(bookId);
    
    // Show success message with book title
    const book = purchasedBooks.find(b => b.id === bookId);
    const bookTitle = book?.title || 'Book';
    toast.success(`${bookTitle} marked as completed! 🎉`);
  };

  const startReadingSession = (bookId: string) => {
    const session = {
      startTime: new Date().toISOString(),
      bookId,
      status: 'reading'
    };
    
    setReadingSessions((prev: {[key: string]: any[]}) => ({
      ...prev,
      [bookId]: [...(prev[bookId] || []), session]
    }));
    
    // Save to localStorage
    const sessions = JSON.parse(localStorage.getItem(`reading_sessions_${bookId}`) || '[]');
    sessions.push(session);
    localStorage.setItem(`reading_sessions_${bookId}`, JSON.stringify(sessions));
  };

  const endReadingSession = (bookId: string) => {
    const currentSessions = readingSessions[bookId] || [];
    if (currentSessions.length > 0) {
      const lastSession = currentSessions[currentSessions.length - 1];
      if (lastSession.status === 'reading') {
        const updatedSession = {
          ...lastSession,
          endTime: new Date().toISOString(),
          status: 'completed',
          duration: new Date().getTime() - new Date(lastSession.startTime).getTime()
        };
        
        const updatedSessions = currentSessions.map(s => 
          s === lastSession ? updatedSession : s
        );
        
        setReadingSessions((prev: {[key: string]: any[]}) => ({
          ...prev,
          [bookId]: updatedSessions
        }));
        
        // Save to localStorage
        localStorage.setItem(`reading_sessions_${bookId}`, JSON.stringify(updatedSessions));
      }
    }
  };

  const getReadingStats = (bookId: string) => {
    const sessions = readingSessions[bookId] || [];
    const completedSessions = sessions.filter(s => s.status === 'completed');
    
    return {
      totalSessions: sessions.length,
      completedSessions: completedSessions.length,
      totalReadingTime: completedSessions.reduce((total, s) => total + (s.duration || 0), 0),
      lastRead: sessions.length > 0 ? sessions[sessions.length - 1].startTime : null
    };
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-x-hidden">
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        {/* Main Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            
            {/* Left Side - Text Content */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Welcome Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <BookOpenIcon className="h-3 w-3 mr-1.5 text-blue-300" />
                Your Personal Library
              </div>

              {/* Main Headline */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-white">
                <span className="block">Master Your</span>
                <span className="block bg-gradient-to-r from-blue-200 via-cyan-200 to-indigo-200 bg-clip-text text-transparent">
                  Reading Journey
                </span>
              </h1>

              {/* Subtitle */}
              <p className="text-base sm:text-lg text-blue-100 leading-relaxed max-w-2xl mx-auto lg:mx-0">
                Track your reading progress, continue where you left off, and achieve your professional goals with expert-curated study materials.
              </p>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2 py-3" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {purchasedBooks.length}
                  </div>
                  <div className="text-xs text-blue-200">Total Books</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {purchasedBooks.length > 0 ? Math.round(purchasedBooks.reduce((total, book) => total + (readingProgress[book.id] || 0), 0) / purchasedBooks.length) : 0}%
                  </div>
                  <div className="text-xs text-blue-200">Avg Progress</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {purchasedBooks.filter(book => readingProgress[book.id] === 100).length}
                  </div>
                  <div className="text-xs text-blue-200">Completed</div>
                </div>
              </div>

              {/* Call-to-Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start" style={{ width: '100%', maxWidth: '100%' }}>
                <button
                  onClick={refreshBooks}
                  disabled={loading}
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg text-sm"
                >
                  <ArrowPathIcon className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
                  Refresh Library
                </button>
                {purchasedBooks.length > 0 && (
                  <button
                    onClick={() => navigate('/app/admin')}
                    className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                  >
                    <BookOpenIcon className="h-4 w-4 mr-1.5" />
                    Add More Books
                  </button>
                )}
              </div>
            </div>

            {/* Right Side - Visual Elements - Hidden on mobile for stability */}
            <div className="relative hidden xl:block">
              {/* Reading Progress Cards */}
              <div className="space-y-4">
                {/* Active Reading Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                      <EyeIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Active Reading</h3>
                      <p className="text-blue-200 text-sm">
                        {purchasedBooks.filter(book => readingProgress[book.id] > 0 && readingProgress[book.id] < 100).length} books in progress
                      </p>
                    </div>
                  </div>
                </div>

                {/* Achievement Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                      <BookOpenIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Completed</h3>
                      <p className="text-blue-200 text-sm">
                        {purchasedBooks.filter(book => readingProgress[book.id] === 100).length} books finished
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Main Dashboard Content */}
      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 pt-0 sm:pt-6 relative z-20 space-y-4 sm:space-y-8">
        {/* Books List */}
        <div className="px-3 sm:px-0">
          {loading ? (
            <div className="text-center py-12">
              <div className="relative">
                <div className="w-16 h-16 mx-auto mb-4">
                  <div className="absolute inset-0 bg-blue-100 rounded-full animate-pulse"></div>
                  <div className="absolute inset-2 bg-white rounded-full flex items-center justify-center">
                    <BookOpenIcon className="h-8 w-8 text-blue-600" />
                  </div>
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Your Library</h3>
              <p className="text-gray-600 mb-3">Gathering your purchased books and study materials...</p>
              <div className="flex justify-center space-x-2">
                <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
                <div className="text-red-600 text-3xl">⚠️</div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Oops! Something went wrong</h3>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={refreshBooks}
                className="px-4 py-2 sm:px-5 sm:py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors font-medium text-sm sm:text-base"
              >
                <ArrowPathIcon className="h-4 w-4 inline mr-2" />
                Try Again
              </button>
            </div>
          ) : purchasedBooks.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                <BookOpenIcon className="h-12 w-12 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">View or create your books or manage</h3>
              <p className="text-gray-600 mb-4 max-w-md mx-auto">
                Access the admin panel to view, create, or manage your books and study materials.
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => navigate('/app/admin')}
                  className="px-4 py-2.5 sm:px-6 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm sm:text-base"
                >
                  <BookOpenIcon className="h-4 w-4 inline mr-2" />
                  Browse Books
                </button>
                <div className="text-sm text-gray-500">
                  Discover books on ICAG, CITG, and general professional topics
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Books Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
                {purchasedBooks.map((book: Book) => {
                  const stats = getReadingStats(book.id);
                  const rawProgress = readingProgress[book.id] || 0;
                  const progress = Math.min(Math.max(rawProgress, 0), 100); // Ensure progress is between 0-100
                  const lastRead = stats.lastRead ? new Date(stats.lastRead) : null;
                  const daysSinceLastRead = lastRead ? Math.floor((Date.now() - lastRead.getTime()) / (1000 * 60 * 60 * 24)) : null;
                  
                  return (
                    <div
                      key={book.id}
                      className="group transform hover:scale-[1.02] transition-all duration-300 cursor-pointer"
                      onClick={() => handleViewBook(book)}
                    >
                      <div className="bg-white rounded-xl shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300 overflow-hidden relative h-[500px] sm:h-[600px]">
                        {/* Book Cover */}
                        <div className="relative w-full h-40 sm:h-48 bg-gray-100 overflow-hidden">
                          {(() => {
                            const imageUrl = getImageUrl(book.coverImage || book.image_url);
                            return imageUrl ? (
                              <img
                                src={imageUrl}
                                alt={book.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                loading="lazy"
                                decoding="async"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  const fallback = e.currentTarget.parentElement?.querySelector('.image-fallback');
                                  if (fallback) {
                                    (fallback as HTMLElement).style.display = 'flex';
                                  }
                                }}
                              />
                            ) : null;
                          })()}
                          
                          {/* Fallback content - shown when no image or image fails to load */}
                          <div className={`image-fallback w-full h-full items-center justify-center ${getImageUrl(book.coverImage || book.image_url) ? 'hidden' : 'flex'}`}>
                            <div className="text-center">
                              <BookOpenIcon className="h-16 w-16 text-gray-400 mx-auto mb-2" />
                              <p className="text-sm text-gray-500 font-medium line-clamp-2 px-2">{book.title}</p>
                            </div>
                          </div>

                          {/* Reading Progress Badge */}
                          <div className="absolute top-2 left-2">
                            <div className="bg-white px-2 py-1 rounded text-gray-800 flex items-center shadow-sm" title={`Page ${Math.max(1, Math.round((progress / 100) * (bookTotalPages[book.id] || 1)))} of ${bookTotalPages[book.id] || '?'}`}>
                              <span className="text-xs font-bold text-blue-600">{progress}%</span>
                            </div>
                          </div>

                          {/* Reading Status Badge */}
                          <div className="absolute top-2 right-2">
                            {progress === 0 ? (
                              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded font-medium shadow-sm">
                                New
                              </span>
                            ) : progress === 100 ? (
                              <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded font-medium shadow-sm">
                                Completed
                              </span>
                            ) : (
                              <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded font-medium shadow-sm">
                                In Progress
                              </span>
                            )}
                          </div>

                          {/* Reading Progress Bar */}
                          <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white p-2">
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="font-medium">Reading Progress</span>
                              <span className="font-bold">{progress}%</span>
                            </div>
                            <div className="w-full bg-gray-700/50 rounded-full h-2" title={`Page ${Math.max(1, Math.round((progress / 100) * (bookTotalPages[book.id] || 1)))} of ${bookTotalPages[book.id] || '?'}`}>
                              <div 
                                className="bg-blue-500 h-2 rounded-full transition-all duration-500 ease-out"
                                style={{ width: `${progress}%` }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Book Content */}
                        <div className="p-3 sm:p-4 flex-1 flex flex-col justify-end">
                          {/* Title */}
                          <h4 className="font-bold text-gray-900 text-sm sm:text-base leading-tight group-hover:text-blue-600 transition-colors">
                            {book.title}
                          </h4>
                          
                          {/* Author */}
                          <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
                            by <span className="font-medium text-gray-700">{book.author || 'Unknown Author'}</span>
                          </p>
                          
                          {/* Tags Row */}
                          <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-3 sm:mb-4">
                            {/* Program Tag */}
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 border border-blue-200">
                              <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              <span className="truncate max-w-[80px]">
                                {book.program || 'General'}
                              </span>
                            </span>
                            
                            {/* Category Tag */}
                            {book.category && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-green-50 text-green-700 border border-green-200">
                                <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM7 7a1 1 0 000 2h6a1 1 0 100-2H7zM7 11a1 1 0 000 2h6a1 1 0 100-2H7z" />
                                </svg>
                                <span className="truncate max-w-[80px]">
                                  {book.category}
                                </span>
                              </span>
                            )}
                          </div>
                          
                          {/* Reading Stats */}
                          <div className="mb-3 sm:mb-4 space-y-1.5 sm:space-y-2">
                            {/* Reading Sessions */}
                            <div className="flex items-center justify-between text-xs text-gray-600 p-2 bg-blue-50 rounded-lg border border-blue-200">
                              <span className="flex items-center">
                                <svg className="h-3 w-3 mr-1.5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                                </svg>
                                <span className="font-medium">Sessions</span>
                              </span>
                              <span className="font-medium text-blue-600">
                                {stats.totalSessions}
                              </span>
                            </div>
                            
                            {/* Reading Time */}
                            <div className="flex items-center justify-between text-xs text-gray-600 p-2 bg-green-50 rounded-lg border border-green-200">
                              <span className="flex items-center">
                                <svg className="h-3 w-3 mr-1.5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                </svg>
                                <span className="font-medium">Time</span>
                              </span>
                              <span className="font-medium text-green-600">
                                {Math.round(stats.totalReadingTime / 60000)}m
                              </span>
                            </div>

                            {/* Last Read */}
                            {lastRead && (
                              <div className="flex items-center justify-between text-xs text-gray-600 p-2 bg-purple-50 rounded-lg border border-purple-200">
                                <span className="flex items-center">
                                  <svg className="h-3 w-3 mr-1.5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                  </svg>
                                  <span className="font-medium">Last Read</span>
                                </span>
                                <span className={`font-medium ${
                                  daysSinceLastRead === 0 ? 'text-green-600' : 
                                  daysSinceLastRead && daysSinceLastRead <= 7 ? 'text-yellow-600' : 'text-red-600'
                                }`}>
                                  {daysSinceLastRead === 0 ? 'Today' : 
                                   daysSinceLastRead === 1 ? 'Yesterday' : 
                                   `${daysSinceLastRead}d ago`}
                                </span>
                              </div>
                            )}
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="w-full relative z-10 mt-auto space-y-1.5 sm:space-y-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewBook(book);
                              }}
                              className="w-full inline-flex items-center justify-center px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white text-xs sm:text-sm font-bold rounded transition-all duration-200"
                            >
                              <EyeIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                              <span className="hidden sm:inline">
                                {progress === 100 ? 'Re-read' : progress > 0 ? 'Continue Reading' : 'Start Reading'}
                              </span>
                              <span className="sm:hidden">
                                {progress === 100 ? 'Re-read' : progress > 0 ? 'Continue' : 'Start'}
                              </span>
                            </button>
                            
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                toast.success(
                                  <div className="space-y-2">
                                    <div className="font-semibold">📊 Reading Statistics</div>
                                    <div className="text-sm space-y-1">
                                      <div>Total Sessions: <span className="font-bold text-blue-600">{stats.totalSessions}</span></div>
                                      <div>Reading Time: <span className="font-bold text-green-600">{Math.round(stats.totalReadingTime / 60000)} minutes</span></div>
                                      <div>Progress: <span className="font-bold text-purple-600">{progress}%</span></div>
                                      {lastRead && (
                                        <div>Last Read: <span className="font-bold text-gray-600">{lastRead.toLocaleDateString()}</span></div>
                                      )}
                                    </div>
                                  </div>,
                                  { duration: 4000 }
                                );
                              }}
                              className="w-full inline-flex items-center justify-center px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs sm:text-sm font-medium rounded border border-gray-200 hover:border-gray-300 transition-all duration-200"
                              title="View detailed reading statistics"
                            >
                              <ChartBarIcon className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                              <span className="hidden sm:inline">View Stats</span>
                              <span className="sm:hidden">Stats</span>
                            </button>
                            
                            {/* Mark as Completed Button - only show if not already completed */}
                            {progress < 100 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markBookAsCompleted(book.id);
                                }}
                                className="w-full inline-flex items-center justify-center px-3 py-1.5 sm:px-4 sm:py-2 bg-green-100 hover:bg-green-200 text-green-700 text-xs sm:text-sm font-medium rounded border border-green-200 hover:border-green-300 transition-all duration-200"
                                title="Mark this book as completed (100% progress)"
                              >
                                <svg className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                <span className="hidden sm:inline">Mark as Completed</span>
                                <span className="sm:hidden">Complete</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {/* Quick Actions Footer */}
              {purchasedBooks.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 text-center">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">Need More Study Materials?</h3>
                  <p className="text-gray-600 mb-4 text-lg">Explore our course catalog to find more books and resources</p>
                  <button
                    onClick={() => navigate('/app/admin')}
                    className="px-4 py-2.5 sm:px-6 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all duration-200 font-medium shadow-md hover:shadow-lg transform hover:scale-105 text-sm sm:text-base"
                  >
                    <BookOpenIcon className="h-4 w-4 sm:h-5 sm:w-5 inline mr-2" />
                    Browse Books
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {showPdfViewer && selectedBook && (
        <div className="fixed inset-0 bg-white overflow-hidden" style={{ height: '', width: '', zIndex: 10000 }}>
          {/* Header */}
        

          {/* Content Area - Full Height */}
          <div className="flex-1 overflow-hidden " style={{ height: '100%', width: '100%' }}>


            {/* PDF Content - Using PdfViewer Component */}
            <div className="flex-1 overflow-y-auto " style={{ height: '100%', width: '100%' }}>
              <div className="max-w-6xl mx-auto">
                {selectedBook?.pdf_url ? (
                  <Suspense fallback={
                    <div className="flex items-center justify-center h-96">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading PDF Viewer...</p>
                      </div>
                    </div>
                  }>
                    <WebViewPdfViewer
                      pdfUrl={selectedBook.pdf_url}
                      title={selectedBook.title || 'PDF Document'}
                      onClose={() => {
                        try {
                          if (selectedBook?.id) {
                            endReadingSession(selectedBook.id);
                          }
                        } catch (e) {
                          // no-op
                        }
                        setShowPdfViewer(false);
                        setSelectedBook(null);
                      }}
                      className="w-full"
                    />
                  </Suspense>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <DocumentTextIcon className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <p className="text-lg font-medium">No PDF content available</p>
                    <p className="text-sm">The document might be empty or contain only images.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AI Chat Button */}
      <AIChatButton />
    </div>
  );
};

export default MyBooks;